import { useCallback, useEffect, useRef, useState } from "react";
import { ClubService } from "../../../../services/clubService";
import { msg } from "../auth/authUtils";

/**
 * Pending club-creation requests + approve/reject, mirroring screens/SuperAdminScreen.js
 * (fetch :90, handleClubCreationRequestAction :114-186).
 * Returns { requests, loading, error, reload, pending, review } where `pending` maps
 * requestId -> "approve" | "reject" while that row's call is in flight.
 */
export default function useClubRequests({ enabled, uid, toast }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pending, setPending] = useState({});
  const epoch = useRef(0);
  const inFlight = useRef(new Set());
  const requestsRef = useRef(requests);
  requestsRef.current = requests;

  const load = useCallback(async ({ initial = false } = {}) => {
    const my = ++epoch.current;
    if (initial) { setLoading(true); setError(""); }
    const { data, error: err } = await ClubService.getClubCreationRequests();
    if (my !== epoch.current) return;
    if (err) {
      if (initial) setError("Failed to load club creation requests");
      else toast("Failed to load club creation requests", "alert");
    } else {
      setRequests(data || []);
      setError("");
    }
    if (initial) setLoading(false);
  }, [toast]);

  useEffect(() => {
    if (!enabled) return undefined;
    const epochRef = epoch;
    load({ initial: true });
    return () => { epochRef.current++; };
  }, [enabled, load]);

  const review = useCallback(async (requestId, action) => {
    if (inFlight.current.has(requestId)) return;
    const snapshot = requestsRef.current.find((r) => r.id === requestId);
    if (!snapshot) return;
    inFlight.current.add(requestId);
    setPending((p) => ({ ...p, [requestId]: action }));
    const restore = () => setRequests((cur) => cur.map((r) => (r.id === requestId ? snapshot : r)));
    // Optimistic mark, reversible for a definitive failure.
    setRequests((cur) => cur.map((r) => (r.id === requestId ? { ...r, _caprPending: true } : r)));
    try {
      if (!uid) {
        restore();
        toast("You must be logged in to review requests", "alert");
        return;
      }
      const { error: err } = await ClubService.reviewClubCreationRequest(requestId, action, uid);
      if (err) {
        if (err.outcome === "ambiguous") {
          await load();
          toast("We could not confirm the result. The request was refreshed; retry only if it is still pending.", "alert");
        } else {
          restore();
          toast(msg(err, "Failed to process request"), "alert");
        }
        return;
      }
      toast(action === "approve"
        ? "Club creation request approved. The club has been created and the requester is now an admin."
        : "Club creation request rejected.");
      await load();
    } catch {
      restore();
      toast("Failed to process request", "alert");
    } finally {
      inFlight.current.delete(requestId);
      setPending((p) => { const n = { ...p }; delete n[requestId]; return n; });
    }
  }, [uid, load, toast]);

  return { requests, loading, error, reload: () => load({ initial: true }), pending, review };
}
